import { isEntityProgramme, isNodeEdgeMapperLike } from '@iotv/datamodel-core';
import { ErrDataReducer, getObjectHash, getOverlapLength, InsertBehavior, isDayMaskDefinition, isUTCPeriodMins, isValidBusinessObject, } from '@iotv/iotv-v3-types';
import { AddEntityToTimeslotInputBehavior, EntityScheduler, } from '@iotv/lfc-devices';
import dayjs from 'dayjs';
import { createContext } from 'react';
import { sequentialSchedulerDAO } from '../data/dataAccessObject';
import { getColourForSlot } from '../fns/colourMap';
const debug = process.env.REACT_APP_DEBUG && false;
export const defaultProgrammeReducerState = {
    ipData: null,
    programme: null,
    programmeSet: null,
    scheduler: null,
    linkItemToProgramRejectionBehavior: AddEntityToTimeslotInputBehavior.ALLOW_IF_LINKED,
    schedulerHash: null,
    fromDateValue: dayjs().startOf('d'),
    toDateValue: dayjs().startOf('d').add(1, 'd'),
    dayMask: null,
    rows: null,
    componentState: {
        itemConflictRecords: null,
    },
};
export const isProgrammeReducerSetIpDataAction = (x) => {
    const c = x ?? {};
    return c.type === 'SetIpData' && !!c.value.ipData && typeof c.value.ipData.utc_offset === 'string';
};
const isProgrammeReducerRecalculateAction = (x) => {
    const c = x ?? {};
    return c.type === 'Recalculate';
};
export const isProgrammeReducerSetProgrammeAction = (x) => {
    const c = x ?? {};
    return c.type === 'SetProgramme' && (c.value.programme === null || isEntityProgramme(c.value.programme));
};
export const isProgrammeReducerMutateProgrammeAction = (x) => {
    const c = x ?? {};
    return c.type === 'MutateProgramme';
};
const isProgammeReducerAddSlotAction = (x) => {
    const c = x ?? {};
    return (c.type === 'AddSlot' &&
        isUTCPeriodMins(c.value.timeslot) &&
        typeof c.value.insertBehavior === 'string' &&
        Object.values(InsertBehavior).includes(c.value.insertBehavior));
};
export const isProgammeReducerRemoveSlotsAction = (x) => {
    const c = x ?? {};
    return c.type === 'RemoveSlots' && c.value.timeslots && c.value.timeslots.every(isUTCPeriodMins);
};
const isProgammeReduceModifySlotAction = (x) => {
    const c = x ?? {};
    return c.type === 'ModifySlot' && isUTCPeriodMins(c.value.oldTimeslot) && isUTCPeriodMins(c.value.newTimeslot);
};
export const isProgrammeReducerLinkItemsToTimeslotsAction = (x) => {
    const c = x ?? {};
    return (c.type === 'LinkItemsToSlots' &&
        c.value.successfulPrechecks.every((preCheck) => {
            const { existingContext, item, timeslot } = preCheck;
            const isNEM = isNodeEdgeMapperLike(existingContext);
            const isItem = isValidBusinessObject(item);
            const isTimeslot = isUTCPeriodMins(timeslot);
            const is = isNEM && isItem && isTimeslot;
            if (!is) {
                console.log(`Not successfulPrecheck`, { preCheck, isNEM, isItem, isTimeslot });
            }
            return is;
        }));
};
const isProgrammeReducerUnlinkItemsFromTimeslotAction = (x) => {
    const c = x ?? {};
    return c.type === 'UnlinkItemsFromSlot' && isUTCPeriodMins(c.value.timeslot);
};
export const isProgrammeReducerUnlinkItemsFromMultipleSlotsAction = (x) => {
    const c = x ?? {};
    return c.type === 'UnlinkItemsFromMultipleSlots' && c.value.timeslots instanceof Array && c.value.timeslots.every(isUTCPeriodMins);
};
const isSetDatesAction = (x) => {
    const c = x ?? {};
    return c.type === 'SetDates' && !!c.value?.fromDateValue && !!c.value?.toDateValue;
};
export const isProgrammeReducerSetDayMaskAction = (x) => {
    const c = x ?? {};
    return c.type === 'SetDaymask' && !!c.value.dayMask && isDayMaskDefinition(c.value.dayMask);
};
export const isProgrammeReducerSetConflictRecordsAction = (x) => {
    const c = x ?? {};
    console.log(`isProgrammeReducerSetConflictRecordsAction`, c);
    return (c.type === 'SetConflictRecords' &&
        ((!!c.value && c.value.itemConflictsRecord === null) ||
            (!!c.value.itemConflictsRecord &&
                isValidBusinessObject(c.value.itemConflictsRecord?.item) &&
                c.value.itemConflictsRecord.conflictingSlots.every((cr) => isUTCPeriodMins(cr.p)))));
};
export const isProgrammeReducerClearConflictRecordsAction = (x) => {
    const c = x;
    return c.type === 'ClearConfictRecords';
};
/** returns a new reducer to avoid possibility of multiple usages using same reducer */
export const getProgrammeReducer = () => {
    return (prev, action) => {
        let update = { ...prev };
        if (isSetDatesAction(action)) {
            const { fromDateValue, toDateValue } = action.value;
            update.fromDateValue = fromDateValue;
            update.toDateValue = toDateValue;
            debug && console.log(`EntityProgrammeReducer got SetDatesAction`, action);
            update = recalculate(update);
        }
        else if (isProgrammeReducerSetProgrammeAction(action)) {
            debug && console.log(`EntityProgrammeReducer got SetProgrammeAction`, action);
            const { value: { programme }, } = action;
            update = defaultProgrammeReducerState;
            update.programme = programme;
            if (programme) {
                update.scheduler = new EntityScheduler({ dataAccess: sequentialSchedulerDAO, programme });
                update.dayMask = programme.dayMask;
            }
            update = recalculate(update);
        }
        else if (isProgrammeReducerMutateProgrammeAction(action)) {
            if (update.scheduler) {
                const { value: { delta }, } = action;
                if (delta) {
                    update.scheduler.mutateProgramme(delta);
                    update.programme = update.scheduler.programme;
                }
            }
        }
        else if (isProgrammeReducerSetDayMaskAction(action)) {
            /** Consumers must call this action with the awaited result of scheduler.changeDayMask as the clash check should not be done here */
            if (update.programme) {
                update.dayMask = action.value.dayMask;
                update = recalculate(update);
            }
            else {
                debug && console.log(`EntityProgrammeReducer no programme for ProgrammeReducerSetDayMaskAction`, action);
            }
        }
        else if (update.scheduler) {
            if (isProgammeReducerAddSlotAction(action)) {
                const { value: { timeslot, insertBehavior }, } = action;
                if (update.scheduler) {
                    update.scheduler.insertTimeslot(timeslot, insertBehavior);
                }
            }
            else if (isProgammeReducerRemoveSlotsAction(action)) {
                const { value: { timeslots }, } = action;
                if (update.scheduler) {
                    timeslots.forEach(update.scheduler.deleteTimeslot);
                }
            }
            else if (isProgammeReduceModifySlotAction(action)) {
                const { value: { oldTimeslot, newTimeslot }, } = action;
                if (update.scheduler) {
                    update.scheduler.modifyTimeslot({ oldTimeslot, newTimeslot });
                }
            }
            else if (isProgrammeReducerLinkItemsToTimeslotsAction(action)) {
                debug && console.log(`EntityProgrammeReducer got isProgrammeReducerLinkItemsToTimeslotsAction`, action);
                const { value: { successfulPrechecks }, } = action;
                if (update.scheduler && successfulPrechecks.every(({ timeslot }) => update.scheduler.getSlotForTimeslot(timeslot))) {
                    debug && console.log(`EntityProgrammeReducer LinkItemsToTimeslotsAction passed validation`, action);
                    const consildatedRes = ErrDataReducer(successfulPrechecks.map(update.scheduler.allocateItemToTimeslot));
                    console.log(`EntityProgrammeReducer link consildatedRes`, consildatedRes);
                }
            }
            else if (isProgrammeReducerUnlinkItemsFromTimeslotAction(action)) {
                const { value: { timeslot }, } = action;
                if (update.scheduler) {
                    const slot = update.scheduler.getSlotForTimeslot(timeslot);
                    if (slot) {
                        const items = update.scheduler.getItemsForSlot(slot);
                        const res = items.map((item) => {
                            return update.scheduler.removeItemFromTimeslot({ timeslot, item });
                        });
                    }
                }
            }
            else if (isProgrammeReducerUnlinkItemsFromMultipleSlotsAction(action)) {
                const { value: { timeslots }, } = action;
                if (update.scheduler) {
                    for (let timeslot of timeslots) {
                        const slot = update.scheduler.getSlotForTimeslot(timeslot);
                        if (slot) {
                            const items = update.scheduler.getItemsForSlot(slot);
                            const res = items.map((item) => {
                                return update.scheduler.removeItemFromTimeslot({ timeslot, item });
                            });
                        }
                    }
                }
            }
            else if (isProgrammeReducerSetIpDataAction(action)) {
                const { value: { ipData }, } = action;
                update.ipData = ipData;
            }
            else if (isProgrammeReducerSetConflictRecordsAction(action)) {
                if (action.value.itemConflictsRecord) {
                    const { value: { itemConflictsRecord: { conflictingSlots, item }, }, } = action;
                    if (conflictingSlots.length > 0) {
                        if (!update.componentState.itemConflictRecords) {
                            update.componentState.itemConflictRecords = {};
                        }
                        update.componentState.itemConflictRecords[item.sk] = { item, conflictingSlots };
                    }
                    else {
                        delete update.componentState.itemConflictRecords?.[item.sk];
                    }
                    debug && console.log(`EntityProgrammeReducer ProgrammeReducerSetConflictRecordsAction set conflictRecords`, action.value.itemConflictsRecord);
                }
            }
            else if (isProgrammeReducerClearConflictRecordsAction(action)) {
                update.componentState.itemConflictRecords = null;
                console.log('reset itemConflictRecords', update.componentState.itemConflictRecords);
            }
            else {
                debug && console.log(`Action fell through reducer`, action);
            }
            update = recalculate(update);
        }
        else {
            debug && console.log(`Got scheduling action when no scheduler present`);
        }
        if (isProgrammeReducerRecalculateAction(action)) {
            debug && console.log('Programme Reducer got recalculate,  handled outside this block');
        }
        if (update.scheduler) {
            const contextHash = update.scheduler.context.getHash();
            const dayMaskHash = getObjectHash(update.scheduler.programme.dayMask);
            const newHash = `${contextHash}_${dayMaskHash}`;
            if (update.schedulerHash !== newHash) {
                update = recalculate(update);
                update.schedulerHash = newHash;
            }
            else {
                debug && console.log(`same hash`);
            }
            if (update.scheduler.programmeSet) {
                update.programmeSet = update.scheduler?.programmeSet ?? null;
            }
            else {
                debug && console.log(`EntityProgrammeReducer skipping update to programmeSet as no programmeSet`);
            }
        }
        else {
            debug && console.log(`EntityProgrammeReducer skipping updates as no scheduler`);
        }
        return update;
    };
};
export const EntityProgrammeContext = createContext([
    defaultProgrammeReducerState,
    (next) => {
        throw new Error('Reducer not initialized');
    },
]);
const recalculate = (update) => {
    if (update.scheduler && update.fromDateValue && update.toDateValue) {
        const referenceRange = [update.fromDateValue.valueOf(), update.toDateValue.valueOf()];
        const slotDateRanges = update.scheduler.getOrderedDateRangesFromSlots(update.fromDateValue.toDate());
        const filteredSlotDateRanges = slotDateRanges.filter((dr) => getOverlapLength([referenceRange, [dr.dr[0].valueOf(), dr.dr[1].valueOf()]]) !== 0);
        const rows = filteredSlotDateRanges.map((slotDateRange) => getRowFromDateRange({ slotDateRange, programme: update.programme, scheduler: update.scheduler }));
        debug && console.log(`generated rows`, { slotDateRanges, rows, dayMask: update.scheduler.programme.dayMask });
        if (rows.length > 0) {
            update.rows = rows;
        }
        else if (update.fromDateValue && update.toDateValue) {
            debug && console.log('no schedules', rows);
            const noDataRows = [[`${update.programme?.name}`, 'No Schedules', 'white', update.fromDateValue?.toDate(), update.toDateValue?.toDate()]];
            update.rows = noDataRows;
        }
    }
    else {
        const { scheduler, fromDateValue, toDateValue } = update;
        debug && console.log(`no recalculation because`, { scheduler, fromDateValue, toDateValue });
    }
    return update;
};
const getRowFromDateRange = ({ programme, scheduler, slotDateRange }) => {
    const { slot, dr } = slotDateRange;
    const scheduledItemNames = scheduler.getItemsForSlot(slot).map((i) => i.name);
    const color = getColourForSlot(programme, scheduler, slot);
    return [`${programme?.name}`, scheduledItemNames.length ? scheduledItemNames.join(', ') : `(No items)`, color, ...dr];
};
